/**
 * ============================================================
 * KEJJA OS — src/pages/settings/PreferencesPage.tsx
 * ============================================================
 */

import { useState, useEffect, type FormEvent } from 'react';
import { Save, Check, Globe, DollarSign, Bell, Palette } from 'lucide-react';
import { useSettingsContext } from './SettingsLayout';
import { useHomePreferences, useUpdateHomePreferences } from '@/hooks/useSettings';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

const TIMEZONES = [
  'Africa/Nairobi',
  'Africa/Lagos',
  'Africa/Kampala',
  'Africa/Dar_es_Salaam',
  'Africa/Johannesburg',
  'Europe/London',
  'Europe/Paris',
  'America/New_York',
  'America/Los_Angeles',
  'Asia/Dubai',
  'Asia/Tokyo',
  'UTC',
];

const CURRENCIES = ['KES', 'UGX', 'TZS', 'USD', 'EUR', 'GBP', 'ZAR', 'NGN'];

const LANGUAGES: Array<{ code: string; label: string }> = [
  { code: 'en', label: 'English' },
  { code: 'sw', label: 'Kiswahili' },
  { code: 'fr', label: 'Français' },
];

import { useTheme as useAppTheme } from '@/hooks/useTheme';

export default function PreferencesPage() {
  const { homeId } = useSettingsContext();
  const { data, isLoading } = useHomePreferences(homeId);
  const updateMut = useUpdateHomePreferences(homeId);

  const [timezone, setTimezone] = useState('Africa/Nairobi');
  const [currencyCode, setCurrencyCode] = useState('KES');
  const [language, setLanguage] = useState('en');
  const { setTheme: applyAppTheme } = useAppTheme();
  const [theme, setTheme] = useState<'system' | 'light' | 'dark'>('system');
  const [notifs, setNotifs] = useState(true);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (data?.preferences) {
      const p = data.preferences;
      setTimezone(p.timezone);
      setCurrencyCode(p.currencyCode);
      setLanguage(p.language);
      setTheme((p.theme as 'system' | 'light' | 'dark') ?? 'system');
      setNotifs(p.notificationsEnabled);
    }
  }, [data]);

  if (isLoading) return <Skeleton className="h-96" />;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setSaved(false);
    updateMut.mutate({
      timezone, currencyCode, language, theme,
      notificationsEnabled: notifs,
    }, { onSuccess: () => { setSaved(true); setTimeout(() => setSaved(false), 2000); } });
  };

  return (
    <div className="pb-12">
      <form onSubmit={submit} className="space-y-4">
        <Card>
          <CardContent className="p-5 space-y-4">
            <h3 className="flex items-center gap-2 text-sm font-semibold">
              <Globe className="w-4 h-4 text-sky-600" /> Locale
            </h3>

            <div>
              <Label className="text-xs">Timezone</Label>
              <select
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
                className="mt-1 w-full h-9 rounded-md border border-border bg-background px-2 text-sm"
              >
                {TIMEZONES.map((tz) => (
                  <option key={tz} value={tz}>{tz}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs flex items-center gap-1">
                  <DollarSign className="w-3 h-3" /> Currency
                </Label>
                <select
                  value={currencyCode}
                  onChange={(e) => setCurrencyCode(e.target.value)}
                  className="mt-1 w-full h-9 rounded-md border border-border bg-background px-2 text-sm"
                >
                  {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>

              <div>
                <Label className="text-xs">Language</Label>
                <select
                  value={language}
                  onChange={(e) => setLanguage(e.target.value)}
                  className="mt-1 w-full h-9 rounded-md border border-border bg-background px-2 text-sm"
                >
                  {LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
                </select>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5 space-y-3">
            <h3 className="flex items-center gap-2 text-sm font-semibold">
              <Palette className="w-4 h-4 text-sky-600" /> Appearance
            </h3>
            <div>
              <Label className="text-xs">Household theme</Label>
              <div className="flex gap-1.5 mt-1">
                {(['system', 'light', 'dark'] as const).map((t) => (
                  <button
                    key={t} type="button"
                    onClick={() => { setTheme(t); applyAppTheme(t); }}
                    className={cn(
                      'flex-1 py-2 rounded-md border text-xs font-medium capitalize',
                      theme === t
                        ? 'border-sky-500/60 bg-sky-500/10 text-sky-700 dark:text-sky-300'
                        : 'border-border text-muted-foreground',
                    )}
                  >
                    {t}
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">
                Default for everyone in the home. Individuals can override in their profile.
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5 space-y-3">
            <h3 className="flex items-center gap-2 text-sm font-semibold">
              <Bell className="w-4 h-4 text-sky-600" /> Notifications
            </h3>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox" checked={notifs}
                onChange={(e) => setNotifs(e.target.checked)}
              />
              <span className="text-sm">Enable notifications for this household</span>
            </label>
            <p className="text-[10px] text-muted-foreground">
              Email + push delivery is planned for v1.1. This toggle is stored now so your preference is ready.
            </p>
          </CardContent>
        </Card>

        <div className="flex items-center gap-2 sticky bottom-4 z-10 bg-background pt-2">
          <Button
            type="submit"
            className="bg-sky-600 hover:bg-sky-700 text-white"
            disabled={updateMut.isPending}
          >
            <Save className="w-3.5 h-3.5 mr-1" />
            {updateMut.isPending ? 'Saving…' : 'Save preferences'}
          </Button>
          {saved && (
            <span className="text-xs text-emerald-600 flex items-center gap-1">
              <Check className="w-3 h-3" /> Saved
            </span>
          )}
        </div>
      </form>
    </div>
  );
}
